/**
 * Developer Mode state — persistent on/off toggle for the diagnostics
 * surface.
 *
 * Mirrors useTheme's pattern: module-level singleton + useSyncExternalStore
 * so only components that call useDeveloperMode() re-render when the
 * flag flips, not every Context consumer.
 *
 * The toggle is invisible by design — no Settings button exposes it.
 * Reached by tapping the app anywhere five times within ~1.5s, handled
 * by DevGate. When on, the DEV pill appears top-right and unlocks the
 * Voice Diagnostics modal + dev annotations throughout the app.
 */
import { useSyncExternalStore } from "react";

const STORAGE_KEY = "meridian-developer-mode";
const isBrowser = typeof document !== "undefined";

function getStored(): boolean {
  if (!isBrowser) return false;
  return localStorage.getItem(STORAGE_KEY) === "1";
}

let current = getStored();
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return current;
}

export function setDeveloperMode(enabled: boolean) {
  current = enabled;
  if (isBrowser) {
    if (enabled) localStorage.setItem(STORAGE_KEY, "1");
    else localStorage.removeItem(STORAGE_KEY);
  }
  listeners.forEach((fn) => fn());
}

export function toggleDeveloperMode() {
  setDeveloperMode(!current);
  return current;
}

export function useDeveloperMode() {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
